<script setup lang="ts">
import { CheckSquare, ChevronDown, CircleX } from 'lucide-vue-next'
import { onMounted, ref, watch } from 'vue'
import { Badge } from '@/foundation/badge'
import { Button } from '@/foundation/button'
import { Checkbox } from '@/foundation/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/foundation/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/foundation/popover'
import { getConsistentColor, getInitials } from '@/utils/utils'

type Option = { id: string | number; name: string; label?: string }

const props = defineProps({
  options: { type: Array as () => Option[], required: true },
  disabled: { type: Boolean, default: false },
  value: { type: Array as () => Option[], default: () => [] },
  placeholder: { type: String, default: 'Search...' },
  emptyMessage: { type: String, default: 'No results found.' },
  defaultOption: { type: String, default: null },
  height: { type: Number, default: 40 },
  class: { type: String, default: '' },
  insideText: { type: String, default: '' },
  showIcon: { type: Boolean, default: false },
})
const emit = defineEmits<{ 'update:value': (value: Option[]) => void }>()

const selected = ref<Option[]>([...props.value])

watch(
  () => props.value,
  (newVal) => {
    selected.value = Array.isArray(newVal) ? [...newVal] : []
  },
  { deep: true },
)

onMounted(() => {
  if (props.defaultOption) {
    const opt = props.options.find((o) => o.label === props.defaultOption || o.name === props.defaultOption)

    if (opt && !selected.value.some((s) => s.id === opt.id)) {
      selected.value.push(opt)
      emit('update:value', [...selected.value])
    }
  }
})

const open = ref(false)

const isChecked = (item: Option) => selected.value.some((s) => s?.id === item?.id)

function toggle(item: Option) {
  const idx = selected.value.findIndex((s) => s?.id === item?.id)

  if (idx > -1) selected.value.splice(idx, 1)
  else selected.value.push(item)

  emit('update:value', [...selected.value])
}

function clearAll() {
  selected.value = []
  emit('update:value', [])
}

function selectAll() {
  selected.value = [...props.options]
  emit('update:value', selected.value)
}
</script>

<template>
  <Popover v-model:open="open" class="w-full">
    <PopoverTrigger
      as-child
      :class="props.class"
      :style="{ '--radix-popper-anchor-width': '100%', height: 'auto', outline: open ? '2px solid black' : 'none' }"
    >
      <Button
        variant="outline"
        :disabled="disabled"
        size="default"
        class="min-h-[2.5rem] px-2 flex items-center justify-between gap-2 hover:bg-transparent shadow-sm"
      >
        <div class="flex items-center gap-1 flex-1 overflow-hidden">
          <span v-if="selected.length === 0" class="text-muted-foreground text-sm font-normal">
            {{ placeholder }}
          </span>
          <template v-else>
            <Badge
              v-for="item in selected.slice(0, 2)"
              :key="item?.id"
              variant="secondary"
              class="text-muted-foreground !bg-background text-xs font-normal hover:text-white flex-shrink-0"
            >
              <div @click.stop.prevent="toggle(item)">
                <CircleX class="mr-1 !h-3 !w-3" />
              </div>
              {{ item?.label || item?.name }}
            </Badge>
            <Badge
              v-if="selected.length > 2"
              variant="secondary"
              class="text-muted-foreground !bg-muted text-xs font-normal flex-shrink-0"
            >
              +{{ selected.length - 2 }} more
            </Badge>
          </template>
        </div>
        <ChevronDown class="size-4 text-muted-foreground flex-shrink-0" />
      </Button>
    </PopoverTrigger>

    <PopoverContent class="p-0" style="width: var(--radix-popover-trigger-width)">
      <Command
        :filter-function="
          (list: any['options'], term) => list.filter((i: any) => i.name.toLowerCase()?.includes(term?.toLowerCase() || ''))
        "
      >
        <CommandInput :placeholder="insideText" />
        <CommandList>
          <CommandEmpty>{{ emptyMessage }}</CommandEmpty>
          <CommandGroup>
            <CommandItem
              v-for="opt in options"
              :key="opt.id"
              :value="opt"
              class="flex items-center gap-2 py-1 px-3 cursor-pointer"
            >
              <Checkbox :id="String(opt.id)" :is-checked="isChecked(opt)" @click="() => toggle(opt)" />
              <div
                v-if="showIcon"
                class="w-5 h-5 rounded-full flex items-center justify-center border border-border bg-light overflow-hidden"
              >
                <div
                  :class="[
                    'w-6 h-6 flex text-xs items-center justify-center text-white font-semibold rounded-full cursor-pointer',
                    getConsistentColor(String(opt?.id || '')),
                  ]"
                >
                  {{ getInitials(opt?.name) }}
                </div>
                <!-- <img
                  v-if="opt?.branding?.logoUrl"
                  :src="opt?.branding?.logoUrl"
                  class="object-cover w-full h-full rounded-full"
                />
                <div v-else class="w-full h-full rounded-full flex items-center justify-center bg-slate-200">
                  <Image :size="18" class="text-muted-foreground" />
                </div> -->
              </div>
              <label :for="String(opt.id)" class="text-sm font-normal cursor-pointer">
                {{ opt.label || opt.name }}
              </label>
            </CommandItem>
          </CommandGroup>
        </CommandList>
        <CommandSeparator />
        <Button
          variant="ghost"
          size="sm"
          class="w-full !rounded-none text-muted-foreground justify-start text-sm font-normal"
          @click="selected.length === 0 ? selectAll() : clearAll()"
        >
          <CheckSquare v-if="selected.length === 0" class="h-4 w-4" />
          <CircleX v-else class="h-4 w-4" />
          {{ selected.length === 0 ? 'Select All' : 'Clear All' }}
        </Button>
      </Command>
    </PopoverContent>
  </Popover>
</template>

<style scoped lang="postcss">
[data-reka-popper-content-wrapper] {
  @apply !pointer-events-auto z-[9999] !min-w-[var(--reka-popper-anchor-width)] !w-[var(--reka-popper-anchor-width)];
}

[data-reka-popper-content-wrapper] > div {
  @apply !w-full;
}
[role='dialog'] button[aria-haspopup='dialog'] {
  .dark & {
    @apply !bg-sidebar-accent;
  }
  .light & {
    @apply !bg-white;
  }
}
</style>
